import Localization from "aldoo-ra/Localization"
import LiveEditRegistry from "aldoo-ra/LiveEdit/registry"

/**
 * Safely clones objects using JSON parse/stringify to avoid issues with functions/JSX elements
 * @param {Object} obj - The object to clone
 * @returns {Object} A deep clone of the input object
 */
const safeClone = (obj) => {
  try {
    // Always use JSON parse/stringify for CMS data to avoid issues with JSX/functions
    return JSON.parse(JSON.stringify(obj))
  } catch (error) {
    console.warn('Failed to clone CMS data, returning original object:', error)
    return obj
  }
}

/**
 * Resolves the localization of a CMS item, with `translate(key)` giving a
 * key's text in the language wanted (undefined when it has none): each
 * localized field takes its translation, nested content is resolved too,
 * and [{ key, value }] lists become maps. What the useCMSLocalization hook
 * does in the browser, and what the server does for a page it renders
 * (./ssr-cms.js) with the translations it fetched for the request.
 * @param {Object} item - The item to localize
 * @param {(key: string) => string|undefined} translate
 * @returns {Object} The localized item
 */
export const resolveCMSLocalization = (item, translate) => {
  const resolveFieldLocalization = (localizedObj, localizationItem) => {
    const { localizationKey, field } = localizationItem
    const translation = translate(localizationKey)
    // Only apply translation if we have a valid one
    if (!translation) return
    // Try content field first, then root-level field
    if (localizedObj.content?.[field]) {
      localizedObj.content[field] = translation
    } else if (localizedObj[field] !== undefined) {
      localizedObj[field] = translation
    }
  }

  const resolveItem = (obj) => {
    if (!obj) return obj

    const localizedObj = safeClone(obj)

    // Handle array of strings case
    if (Array.isArray(localizedObj)) {
      return localizedObj.map((item) => resolveItem(item))
    }

    // Process localization array if present
    if (
      Array.isArray(localizedObj.localization) &&
      localizedObj.localization.length
    ) {
      localizedObj.localization.forEach((localizationItem) => {
        resolveFieldLocalization(localizedObj, localizationItem)
      })
    }

    // Recursively process nested content
    if (localizedObj.content) {
      Object.entries(localizedObj.content).forEach(([key, value]) => {
        const isArrayOfStrings =
          Array.isArray(value) && value.length && typeof value[0] === "string"

        // Handle array of key-value objects (like texts and texts_mobile)
        const isKeyValueArray = Array.isArray(value) && value.length && 
          value[0] && typeof value[0] === "object" && 
          value[0].key && value[0].value !== undefined

        if (isKeyValueArray) {
          // Convert array of {key, value} objects to direct object properties
          const convertedObj = {}
          value.forEach(item => {
            if (item.key && item.value !== undefined) {
              convertedObj[item.key] = item.value
            }
          })
          localizedObj.content[key] = convertedObj
        } else if (!isArrayOfStrings && typeof value === "object") {
          localizedObj.content[key] = resolveItem(value)
        }
      })

      // Preserve ID in content if present
      if (localizedObj._id) localizedObj.content._id = localizedObj._id
    }

    // LiveEdit: record provenance of every localized field (final value).
    if (Array.isArray(localizedObj.localization) && localizedObj.localization.length) {
      try {
        localizedObj.localization.forEach(({ localizationKey, field }) => {
          const finalValue = localizedObj.content?.[field] ?? localizedObj[field]
          LiveEditRegistry.registerItem(localizationKey, field, finalValue)
        })
      } catch (_) { /* never break rendering */ }
    }

    // If there's an _id and content exists, return content to avoid field collision
    // Otherwise return the original fallback behavior
    if (localizedObj._id && localizedObj.content && typeof localizedObj.content === 'object') return localizedObj.content
    return localizedObj.content ?? localizedObj
  }

  return resolveItem(safeClone(item))
}

/** Every localization key an item (or a list of them) references, however deep. */
export const extractCMSLocalizationKeys = (item) => {
  const keys = new Set()
  const extractKeys = (obj) => {
    if (!obj) return
    if (Array.isArray(obj.localization)) {
      obj.localization.forEach(({ localizationKey }) => localizationKey && keys.add(localizationKey))
    }
    Object.values(obj).forEach((value) => {
      if (value && typeof value === "object") extractKeys(value)
    })
  }
  extractKeys(item)
  return Array.from(keys)
}

/**
 * Custom hook for CMS localization functionality
 * @returns {Object} Object containing readLocalization and resolveLocalization functions
 */
const useCMSLocalization = () => {
  const { load, t, translateWithLanguage, currentLanguage, getLanguage } = Localization()

  /**
   * Recursively extracts localization keys from an item
   * @param {Object} item - The item to extract keys from
   * @returns {Array<string>} Array of unique localization keys
   */
  const extractLocalizationKeys = (item) => {
    const keys = new Set()

    const extractKeys = (obj) => {
      if (!obj) return

      if (Array.isArray(obj.localization)) {
        obj.localization.forEach(({ localizationKey }) => {
          keys.add(localizationKey)
        })
      }

      Object.values(obj).forEach((value) => {
        if (value && typeof value === "object") {
          extractKeys(value)
        }
      })
    }

    extractKeys(item)
    return Array.from(keys)
  }

  /**
   * Loads localization data for the provided items
   * @param {Array<Object>} data - Array of items requiring localization
   */
  const readLocalization = async (data) => {
    const keysToLoad = new Set()

    data.forEach((item) => {
      const keys = extractLocalizationKeys(item)
      keys.forEach((key) => keysToLoad.add(key))
    })

    const uniqueKeys = Array.from(keysToLoad)
    await load(uniqueKeys)

    // Load English fallback for non-English languages
    // Skip on client-side if we have SSR data (to prevent API requests)
    // Always load on server-side for proper translation fallbacks
    const isServerSide = typeof window === 'undefined'
    const hasSSRData = !isServerSide && (
      window.__INITIAL_LOCALIZATION_DATA__ || 
      window.__HAD_INITIAL_LOCALIZATION_DATA__
    )
    
    if (getLanguage() !== "en" && (isServerSide || !hasSSRData)) {
      await load(uniqueKeys, "en")
    }
  }

  /**
   * A key's text in the current language, else in English; undefined when
   * neither has it (a missing key reads as {{key}})
   */
  const translateField = (localizationKey) => {
    let translation = t(localizationKey)
    if (translation === `{{${localizationKey}}}`) {
      translation = translateWithLanguage(localizationKey, "en")
    }
    // translateWithLanguage echoes the key back when no language has it
    if (!translation || translation === localizationKey || translation === `{{${localizationKey}}}`) return undefined
    return translation
  }

  /**
   * Recursively resolves localization for an item
   * @param {Object} item - The item to localize
   * @returns {Object} The localized item
   */
  const resolveLocalization = (item) => resolveCMSLocalization(item, translateField)

  return {
    readLocalization,
    resolveLocalization,
  }
}

export default useCMSLocalization
